'use client';

import {CaptchaContextProvider} from '@shared/Captcha/Client/CaptchaContext';
import {retryPromise} from '@utils/retryPromise';
import {registerStoreForEnvVariablesAccess} from '@web-catalog/env';
import {createStore, Provider as JotaiProvider} from 'jotai';
import dynamic from 'next/dynamic';
import {FC, PropsWithChildren, useMemo, useRef} from 'react';

import {OmicronDTO} from '@web-catalog/api';

import {AppCategory} from '@web-catalog/types/category';
import {EnvConfig} from '@web-catalog/types/env';

import {YandexProvider} from '@shared/Ads/Yandex/store';
import {HydrateAtoms} from '@web-catalog/store';

import {MultiProviderFloorAd} from '../Ads/MultiProviderFloorAd';
import {AdFoxController} from '../Ads/Yandex/AdFox/AdFoxController';
import {OmicronClientInitializer} from '../OmicronClient';
import {PageViewReporter} from '../PageViewReporter/PageViewReporter';
import HeadlessUiDocumentPaddingObserver from './HeadlessUiDocumentPaddingObserver';

const ExternalUrlModalController = dynamic(
  retryPromise(
    () =>
      import(
        /* webpackChunkName: "externalUrlModal" */ '@web-catalog/components/Linkify/ExternalUrlModalController'
      ),
    {fallback: {default: () => <></>}},
  ),
  {ssr: false},
);

type Props = PropsWithChildren<{
  envConfig: EnvConfig;
  categories?: {
    content: Array<AppCategory>;
    contentGames: Array<AppCategory>;
  };
  omicron: OmicronDTO;
}>;

const useInitProvider = () => {
  const {current: store} = useRef(createStore());
  registerStoreForEnvVariablesAccess(store);

  return store;
};

export const Providers: FC<Props> = ({
  children,
  envConfig,
  categories,
  omicron,
}) => {
  const store = useInitProvider();

  const categoriesInit = useMemo(
    () => ({
      appsCategories: categories?.content ?? [],
      gamesCategories: categories?.contentGames ?? [],
    }),
    [categories],
  );

  return (
    <JotaiProvider store={store}>
      <HydrateAtoms
        envConfig={envConfig}
        categories={categoriesInit}
        omicron={omicron}
      >
        <YandexProvider>
          <HeadlessUiDocumentPaddingObserver />

          <OmicronClientInitializer omicron={omicron} />

          <CaptchaContextProvider
            allowBypassCaptcha={envConfig.ALLOW_BYPASS_CAPTCHA === 'true'}
            provider={omicron.config.CAPTCHA_PROVIDER}
            yandexSiteKey={envConfig.YANDEX_CAPTCHA_CLIENT_ID}
            vkInitEndpoint="/catalog/api/vk-captcha-init"
          >
            {children}
          </CaptchaContextProvider>

          <MultiProviderFloorAd />

          <PageViewReporter />
          <AdFoxController />
          {omicron.config.ENABLE_URLS && <ExternalUrlModalController />}
        </YandexProvider>
      </HydrateAtoms>
    </JotaiProvider>
  );
};
